import { type ReactNode, useId } from "react";

import { useField } from "@rvf/react-router";

import {
  FieldError,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/forms/Form";
import { useRegisterFieldDisplayName } from "~/components/forms/field-display-names";
import { PHONE_INPUT_MAX_LENGTH, formatUsPhoneInput } from "~/lib/phone";
import { cn } from "~/lib/ui";

import type { BoxedFieldProps } from "./types";

/**
 * Molecule: a white, borderless boxed field whose label is its placeholder —
 * the Become a Partner design (COM-737; the live Fastr page draws exactly
 * this: white boxes on a light-grey panel, "Company Name*"-style placeholders).
 * The house `InputField` is an underline floating-label field and reads as a
 * different control there.
 *
 * A placeholder alone is not an accessible name and vanishes on input, so the
 * label is ALSO the control's `aria-label` (with "(required)" when it is).
 * Validation display is RVF's (`FormMessage`); `liveError` is the optional
 * as-you-type message for the one field that has one.
 */
const FIELD_CLASS =
  "block w-full rounded-none border-0 bg-white px-5 font-grotesque text-base font-light text-foreground placeholder:text-neutral-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-error";

export function BoxedField(props: BoxedFieldProps) {
  const {
    name,
    label,
    required = false,
    liveError,
    disabled,
    "data-testid": testId,
  } = props;
  const field = useField(name);
  const liveId = useId();
  useRegisterFieldDisplayName(name, label);
  const shownLive = field.error() ? null : (liveError ?? null);
  const placeholder = required ? `${label}*` : label;
  // Spread only when set: `Slot` lets a child's `undefined` override
  // `FormControl`'s own aria wiring.
  const liveAria = shownLive
    ? { "aria-invalid": true, "aria-describedby": liveId }
    : {};

  let control: ReactNode;
  if (props.as === "textarea") {
    const {
      as: _as,
      name: _name,
      label: _label,
      required: _required,
      liveError: _liveError,
      disabled: _disabled,
      "data-testid": _testId,
      className,
      ...rest
    } = props;
    control = (
      <textarea
        {...rest}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(FIELD_CLASS, "min-h-[6.125rem] resize-y py-3", className)}
        {...liveAria}
        data-testid={testId}
      />
    );
  } else {
    const {
      as: _as,
      mask,
      name: _name,
      label: _label,
      required: _required,
      liveError: _liveError,
      disabled: _disabled,
      "data-testid": _testId,
      className,
      onChange,
      ...rest
    } = props;
    const phone = mask === "us-phone";
    control = (
      <input
        type={phone ? "tel" : "text"}
        {...(phone
          ? {
              inputMode: "numeric" as const,
              // `tel-national`, not `tel`: an autofilled "+1 555…" would lose
              // its LAST digit to the 10-digit mask and still validate.
              autoComplete: "tel-national",
              maxLength: PHONE_INPUT_MAX_LENGTH,
            }
          : {})}
        {...rest}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(FIELD_CLASS, "h-[2.8125rem]", className)}
        onChange={e => {
          // Same uncontrolled write-back as `PhoneField`: the formatted value
          // is on the node before RVF (merged by `Slot`, after this) reads it.
          if (phone) e.target.value = formatUsPhoneInput(e.target.value);
          onChange?.(e);
        }}
        {...liveAria}
        data-testid={testId}
      />
    );
  }

  return (
    <FormField name={name} className="my-0">
      <FormItem className="space-y-0">
        <FormControl
          aria-label={required ? `${label} (required)` : label}
          {...(required ? { "aria-required": true } : {})}
        >
          {control}
        </FormControl>
        <FormMessage className="px-0 text-left" />
        {shownLive && (
          <FieldError id={liveId} className="pt-2 text-left">
            {shownLive}
          </FieldError>
        )}
      </FormItem>
    </FormField>
  );
}
